/**
 * The campaign monitoring overview (platform design §6.4, §6.5; Figma `125:9` normal, `175:5249`
 * blocked). Three cards read CO-5's campaign-scoped routes (`useAgenda`, `useFailures`,
 * `useCollectionSummary`); the blocked variant is driven by the tenant's own funded-window
 * signal (`acquisitionState`, already landed under `tenant/balance` -- not re-derived here,
 * `[one-grammar-across-repos]`), read only to decide `blocked`.
 *
 * **The blocked StatGrid's four figures have no data source in this plan's scope** -- no route
 * (CO-5's three, or the tenant funded-window one) returns an hourly/daily predicted-observation
 * or estimated-consumption rate; grepping `core`/`api` found none either. Same shape as
 * `VolumeCard` and `OverviewStatGrid`'s consumption card: render `not_measured` rather than
 * fabricate one, flagged here for whoever scopes the real data source.
 */

import type { ReactNode } from 'react';
import { useMemo } from 'react';

import { Absent } from '../../../chrome/measure';
import { useViewSearch } from '../../../chrome/viewSearch';
import { Page, PageHeader, PageSection, StatCard, StatGrid } from '../../../chrome/page';
import { getLocale } from '../../../i18n';
import { credits as formatCredits, integer } from '../../../i18n/format';
import { acquisitionState, useCredits, useFundedWindow } from '../../tenant/balance';
import { AgendaPreviewCard } from './AgendaPreviewCard';
import { useAgenda, useAudio, useCollectionSummary, useFailures, type Window } from './api/queries';
import { AudioCard } from './AudioCard';
import { FailuresPreviewCard } from './FailuresPreviewCard';
import { cm } from './messages';
import { MissedSlotCard } from './MissedSlotCard';
import { VolumeCard } from './VolumeCard';
import { WINDOW_OPTIONS, windowFor, type WindowOption } from './WindowSelect';

function useTodayWindow(): Window {
  return useMemo(() => {
    const now = new Date();
    const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { from: startOfDay.toISOString(), to: now.toISOString() };
  }, []);
}

export function MonitoringOverview(props: {
  campaignId: string;
  tenantId: string;
  organisationName: string;
  /** Pins "now" so a test renders the same day word on every run -- same shape as `Agenda`'s
   *  own `now` prop. */
  now?: Date;
}): ReactNode {
  const { campaignId, tenantId } = props;
  const locale = getLocale();

  const todayWindow = useTodayWindow();
  const [values, set] = useViewSearch(`monitoring.${campaignId}`, {
    window: { default: 'next24h', values: WINDOW_OPTIONS },
  });
  const windowOption = values.window as WindowOption;
  const setWindowOption = (next: WindowOption): void => set({ window: next });
  const agendaWindow = useMemo(
    () => windowFor(windowOption, props.now ?? new Date()),
    [windowOption, props.now],
  );

  const credits = useCredits(tenantId);
  const fundedWindow = useFundedWindow(tenantId);
  const state = acquisitionState(credits.data, fundedWindow.data);
  const blocked = state === 'waiting-on-credits';

  const summary = useCollectionSummary(campaignId, todayWindow);
  const failures = useFailures(campaignId, todayWindow);
  const agenda = useAgenda(campaignId, agendaWindow);
  const audio = useAudio(campaignId);

  return (
    <Page>
      <PageHeader title={cm('title')} />
      <PageSection>
        {blocked ? (
          <StatGrid>
            <StatCard
              label={cm('stats.predictedHourly')}
              value={<Absent reason="not_measured" />}
            />
            <StatCard label={cm('stats.predictedDaily')} value={<Absent reason="not_measured" />} />
            <StatCard
              label={cm('stats.consumptionHourly')}
              value={<Absent reason="not_measured" />}
            />
            <StatCard
              label={cm('stats.consumptionDaily')}
              value={<Absent reason="not_measured" />}
            />
          </StatGrid>
        ) : (
          <StatGrid>
            <StatCard
              label={cm('stats.completed')}
              value={
                summary.data?.completed != null ? (
                  integer(summary.data.completed, locale)
                ) : (
                  <Absent reason={{ text: cm('stats.completed') }} />
                )
              }
            />
            <StatCard
              label={cm('stats.failed')}
              value={
                summary.data?.failed != null ? (
                  integer(summary.data.failed, locale)
                ) : (
                  <Absent reason={{ text: cm('stats.failed') }} />
                )
              }
            />
            <StatCard
              label={cm('stats.creditsUsed')}
              value={
                summary.data?.credits_used != null ? (
                  formatCredits(summary.data.credits_used, locale)
                ) : (
                  <Absent reason={{ text: cm('stats.creditsUsed') }} />
                )
              }
            />
          </StatGrid>
        )}
      </PageSection>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {blocked ? (
          <MissedSlotCard
            tenantId={tenantId}
            organisationName={props.organisationName}
            query={agenda}
            now={props.now}
          />
        ) : (
          <FailuresPreviewCard campaignId={campaignId} query={failures} />
        )}
        <VolumeCard query={summary} />
      </div>
      <AgendaPreviewCard
        campaignId={campaignId}
        query={agenda}
        blocked={blocked}
        windowOption={windowOption}
        onWindowChange={setWindowOption}
      />
      <AudioCard campaignId={campaignId} query={audio} />
    </Page>
  );
}
